<!-- ToDo - Rename to Code Verification or something more generic -->
<template>
    <section class="sms-verification">
        <div v-if="isResendTimerVisible && timerSeconds > 0"
            class="timer"
        >
            00:{{ timerSeconds }}
        </div>

        <div class="widget__fields">
            <label :class="[
                'label',
                {
                    'label--disabled': disabled,
                    'label--loading': isLoading
                }
            ]"
            >
                {{ $t('registerCode.sendCode') }}:

                <input id="input-sms-code"
                    v-model="form.verificationCode"
                    :disabled="disabled || isLoading"
                    :placeholder="$t('registerCode.codePrompt')"
                    type="text"
                    :class="['label__input', { 'label__input--active': form.verificationCode }]"
                    @input="validateCode"
                >

                <div v-if="error.isVisible"
                    class="label__error"
                >
                    {{ error.message }}
                </div>
            </label>
        </div>
    </section>
</template>

<script>
// 3rd party
import axios from 'axios';

// Store
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Local Modules
import localStoreTypes from '@webapp/store/types.js';

// Shared Modules
import sharedEpicTypes from '@shared/epics/types.js';
import sharedStoreTypes from '@shared/store/types.js';
import {
    EventNames,
} from '@shared/utils/analytics-keys';
// Helpers
import apiUrl from '@webapp/helpers/api-url.js';
import {
    scrollToElement,
    storage,
} from '@webapp/helpers/generic.js';

// Config
import {
    hasUserAccountLockedError,
} from '@shared/utils/error.js';

export default {
    name: 'sms-verification',
    props: {
        isLogin: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isLoading: false,
            isResendTimerVisible: false,
            // The timer is set based on the environment and mode (dev / build)
            timerSeconds: process.env.VUE_APP_RESEND_CODE_TIME,
            initialTimerSeconds: process.env.VUE_APP_RESEND_CODE_TIME,
            error: {
                isVisible: false,
                message: '',
            },
            form: {
                verificationCode: '',
            },
            timerInterval: 0,
            login: {},
        };
    },
    computed: {
        ...mapGetters([
            'registrationDetails',
            'device',
            'isUserActive',
            'getLatestSimpleValidationError',
            'partnerLoginFailed',
            'isPartnerApp',
            'partnerAppData',
            'userId',
        ]),
    },
    watch: {
        disabled(newValue) {
            if (newValue === true) {
                this.form.verificationCode = '';
            }
        },
        getLatestSimpleValidationError(error) {
            if (!this.partnerLoginFailed) {
                const fetchErrors = this.utilGlobalHandleErrorMessages(error);

                this.$notify({
                    type: 'error',
                    text: fetchErrors.message,
                });
            }
        },
        isResendTimerVisible(value) {
            if (value) {
                this.$nextTick(() => {
                    document.getElementById('input-sms-code').focus();
                });
            }
        },
        partnerLoginFailed(value) {
            if (value) {
                this.showAppLoader({
                    isVisible: false,
                });
                this.$router.push('/login');
                this.$notify({
                    type: 'error',
                    text: this.$t('registerMobileNumber.loginFailure'),
                });
            }
        },
    },
    async created() {
        await this.detectPartnerLogin();
    },
    methods: {
        ...mapActions(['detectPartnerLogin', 'detectPartnerApp']),

        ...mapActions({
            setUserDetails: sharedStoreTypes.SET_USER_DETAILS,
            setUserSession: sharedStoreTypes.SET_USER_AUTH,
            setRegistration: localStoreTypes.SET_REGISTRATION,
            setRegisterWidgetEnabled: localStoreTypes.SET_IS_REGISTER_WIDGET_ENABLED,
            setDeviceDetails: localStoreTypes.SET_DEVICE_DETAILS,
            showAppLoader: localStoreTypes.SET_APP_LOADER,
            doAppDataRefresh: sharedEpicTypes.REFRESH_APP_DATA,
            logout: sharedStoreTypes.REMOVE_USER_AUTH,
        }),

        ...mapActions('serverConfig', ['fetchServerConfig']),

        startCountdown() {
            let counter = this.initialTimerSeconds;

            this.$emit('timerStarted');
            this.isResendTimerVisible = true;
            this.timerSeconds = counter;

            this.timerInterval = setInterval(() => {
                this.timerSeconds = (counter < 10) ? '0' + counter : counter;
                counter--;

                if (counter < 0) {
                    this.isResendTimerVisible = false;
                    this.$emit('timerExpired');

                    clearInterval(this.timerInterval);
                }
            }, 1000);
        },
        validateCode() {
            this.error = {
                isVisible: false,
                text: '',
            };

            if (this.form.verificationCode.trim().length === 6) {
                this.isLoading = true;

                if (this.isLogin) {
                    this.$webTrackingInstance?.sendEvent(EventNames.LoginSms);
                    this.validateLogin();
                } else {
                    this.$webTrackingInstance?.sendEvent(EventNames.SignUpSms);
                    this.validateRegister();
                }
            }
        },
        async validateLogin() {
            try {
                const login = await axios.post(`${apiUrl()}/user/login/mobile`, {
                    mobileNumber: this.registrationDetails.formattedPhoneNumber,
                    generatedCode: this.form.verificationCode,
                    deviceUid: this.device.uuid,
                    deviceName: this.device.name,
                });

                if (!login || login.errors) {
                    throw login.errors;
                }

                this.login = login.data.data;
                this.isResendTimerVisible = false;

                await this.setUserSession(this.login);

                const userDetails = await axios.get(`${apiUrl()}/user-service/me?details[]=kycTierLevel&details[]=identity`);

                await this.setUserDetails(userDetails.data.data);

                if (!this.isUserActive) {
                    await this.logout();

                    this.showAppLoader({
                        isVisible: false,
                    });

                    this.$notify({
                        type: 'error',
                        text: this.$t('global.deactivatedAccount'),
                    });

                    return;
                }

                await this.doAppDataRefresh();

                this.showAppLoader({
                    isVisible: true,
                });

                if (Object.keys(this.partnerAppData).length) {
                    const organization = this.partnerAppData.id;

                    await this.fetchServerConfig({
                        organization,
                    });
                } else {
                    await this.fetchServerConfig();
                }

                // Reset KYC identity verification alert visiblity to always show on login
                storage('set', 'verificationAlertVisibility', true);

                if (this.isPartnerApp) {
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.PartnerLogin,
                        [
                            {
                                key: 'partner',
                                value: this.partnerAppData.id,
                            },
                            {
                                key: 'user',
                                value: this.userId,
                            },
                        ]
                    );
                }

                await this.detectPartnerApp();

                // Checks for original requested url
                const redirectUrl = localStorage.getItem('originUrl');

                if (redirectUrl) {
                    localStorage.removeItem('originUrl');
                    this.$router.replace(redirectUrl);
                    this.showAppLoader({
                        isVisible: false,
                    });
                } else {
                    this.$router.push('/home');
                    this.showAppLoader({
                        isVisible: false,
                    });
                }
                this.$webTrackingInstance?.sendEvent(EventNames.LoginEnds);
            } catch (error) {
                this.$webTrackingInstance?.sendEvent(EventNames.LoginSmsError);
                if (error.response && hasUserAccountLockedError({
                    status: error.response.status,
                    errors: error.response.data.errors,
                })) {
                    this.error = {
                        isVisible: true,
                        message: this.$t('login.lockedAccount'),
                    };
                    this.$emit('blockInput');

                    return;
                }

                if (!this.partnerLoginFailed) {
                    const fetchErrors = this.utilGlobalHandleErrorMessages(error);

                    this.$notify({
                        type: 'error',
                        text: fetchErrors.message,
                    });
                }
            } finally {
                this.isLoading = false;
            }
        },
        async validateRegister() {
            this.error.isVisible = false;

            try {
                const result = await axios.post(`${apiUrl()}/registration/user/validation/mobileNumber/code`, {
                    mobileNumber: this.registrationDetails.formattedPhoneNumber,
                    generatedCode: this.form.verificationCode,
                });

                if (!result.data.data.isValid) {
                    this.$webTrackingInstance?.sendEvent(EventNames.SignUpSmsError);
                    this.error.isVisible = true;
                    this.error.message = this.$t('registerCode.invalidCode');

                    return;
                }

                this.setRegistration([
                    {
                        property: 'registrationCode',
                        value: this.form.verificationCode,
                    },
                ]);

                this.isResendTimerVisible = false;
                this.setRegisterWidgetEnabled(true);
                scrollToElement('register');
            } catch (error) {
                if (!this.partnerLoginFailed) {
                    const fetchErrors = this.utilGlobalHandleErrorMessages(error);

                    this.$notify({
                        type: 'error',
                        text: fetchErrors.message,
                    });
                }
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.widget__fields {
    margin-top: 2.5rem;
}

.timer {
    text-align: center;
    font-size: 1.5rem;
    font-weight: 700;
}
</style>